import {
  END_OF_SURVEY_ALLOWING_AUTO_REVIEW,
  isEndOfSurvey
} from '@components/GQSurveyBuilder/components/SkipLogicSlideOut';

export type OutcomeTagType = 'ideal' | 'disqualifying' | 'skip_to';

export type RuleOutcome = {
  tag_type: OutcomeTagType | '';
  tag_value: AnswerTagLogic['tag_value'];
  ignore_disqualified?: boolean;
};

export type RuleLogic = {
  operator: AnswerTagLogic['operator'] | '';
  answer_value: AnswerTagLogic['answer_value'];
};

export type Rule = {
  logic: RuleLogic;
  outcomes: RuleOutcome[];
};

// Factory functions for creating clean rule structures
export const createEmptyRule = (): Rule => ({
  logic: {
    operator: '',
    answer_value: ''
  },
  outcomes: [createEmptyOutcome()]
});

export const createEmptyOutcome = (): RuleOutcome => ({
  tag_type: '',
  tag_value: ''
});

export const EMPTY_RULE: Rule = createEmptyRule();

// Constants for rule validation
const MAX_OUTCOMES_PER_RULE = 2;
const ARRAY_BASED_OPERATORS = ['matches_all', 'matches_any', 'match_not_exact', 'matches_none'];
const ARRAY_BASED_FIELD_TYPES = ['multiple_choice'];

export const answerValueIsArray = (field_type: ScreenerFieldType, answerOperator: Rule['logic']['operator']) =>
  ARRAY_BASED_FIELD_TYPES.includes(field_type) || ARRAY_BASED_OPERATORS.includes(answerOperator || '');

const processEndOfSurveyTagValue = (
  tag_type: string,
  tag_value: AnswerTagLogic['tag_value']
): AnswerTagLogic['tag_value'] => {
  if (tag_type === 'skip_to' && isEndOfSurvey(Number(tag_value))) {
    return END_OF_SURVEY_ALLOWING_AUTO_REVIEW;
  }
  return tag_value;
};

const areAnswerValuesEqual = (
  value1: AnswerTagLogic['answer_value'],
  value2: AnswerTagLogic['answer_value']
): boolean => {
  if (Array.isArray(value1) && Array.isArray(value2)) {
    return value1.length === value2.length && value1.every((item, index) => item === value2[index]);
  }
  return value1 === value2;
};

const canGroupWithExistingRule = (
  group: Rule,
  operator: AnswerTagLogic['operator'],
  answer_value: AnswerTagLogic['answer_value'],
  tag_type: string
): boolean => {
  return (
    group.logic.operator === operator &&
    areAnswerValuesEqual(group.logic.answer_value, answer_value) &&
    group.outcomes.length < MAX_OUTCOMES_PER_RULE &&
    !group.outcomes.some((outcome) => outcome.tag_type === tag_type)
  );
};

export function getGroupedRules(rules: AnswerTagLogic[]): Rule[] {
  const grouped: Rule[] = [];

  for (const rule of rules) {
    const { operator, answer_value, tag_type, tag_value } = rule;
    const processedTagValue = processEndOfSurveyTagValue(tag_type, tag_value);

    // Find an existing group that has the same logic conditions
    // but doesn't already have the same tag_type (to avoid duplicates)
    const existingGroup = grouped.find((group) => canGroupWithExistingRule(group, operator, answer_value, tag_type));

    const outcome: RuleOutcome = {
      tag_type: tag_type === 'ignore_disqualified' ? 'disqualifying' : tag_type,
      tag_value: processedTagValue,
      ignore_disqualified: tag_type === 'ignore_disqualified'
    } as RuleOutcome;

    if (existingGroup) {
      existingGroup.outcomes.push(outcome);
    } else {
      // Create a new rule for each distinct logical condition or conflicting tag_type
      grouped.push({
        logic: { operator, answer_value },
        outcomes: [outcome]
      });
    }
  }

  return grouped;
}

export function getFlattenedRules(rules: Rule[]): AnswerTagLogic[] {
  const isNotEmptyLogic = (logic: RuleLogic): logic is Pick<AnswerTagLogic, 'operator' | 'answer_value'> =>
    logic.operator !== '';

  const isNotEmptyOutcome = (outcome: RuleOutcome) => outcome.tag_type !== '';

  const convertIgnoreDisqualified = (outcome: RuleOutcome): Pick<AnswerTagLogic, 'tag_type' | 'tag_value'> => ({
    tag_type: outcome.ignore_disqualified ? 'ignore_disqualified' : (outcome.tag_type as AnswerTagLogic['tag_type']),
    tag_value: outcome.tag_value
  });

  const validRules = rules.filter((rule) => isNotEmptyLogic(rule.logic) && rule.outcomes.some(isNotEmptyOutcome));

  return validRules.reduce<AnswerTagLogic[]>((acc, { logic, outcomes }) => {
    const validOutcomes = outcomes
      .filter(isNotEmptyOutcome)
      .map(convertIgnoreDisqualified)
      .map(
        (outcome): AnswerTagLogic => ({
          operator: logic.operator as AnswerTagLogic['operator'],
          answer_value: logic.answer_value,
          tag_type: outcome.tag_type as AnswerTagLogic['tag_type'],
          tag_value: outcome.tag_value
        })
      );

    return acc.concat(validOutcomes);
  }, []);
}
